import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { select, Store } from '@ngrx/store'
import { Subject } from 'rxjs'
import { localeCompareSort, isEmptyTable } from '@tokeet-frontend/tv3-platform'
import { map, startWith, switchMap, takeUntil, tap } from 'rxjs/operators'
import { FilterGroup, FiltersService } from '@tv3/services/utils/filters.service'
import * as R from 'ramda'
import {
  ConfirmDialogService,
  isSomething,
  LoadUsers,
  Rental,
  selectAllRentals,
  selectUsersLoaded,
  sortAscend,
  Toaster,
} from '@tokeet-frontend/tv3-platform'
import * as fromRoot from '@tv3/store/state'
import { TableType } from '@tv3/shared/empty-table/table-type'

import { SelectableRow } from '@tokeet-frontend/tv3-platform'
import * as lodash from 'lodash'
import { UserStorage } from '@tokeet-frontend/tv3-platform'
import { selectAllCustomCodeViews } from '@tv3/store/custom-codes/custom-code.selectors'
import { DeleteCustomCode, GetAllCustomCodes } from '@tv3/store/custom-codes/custom-code.actions'
import { ModifyCustomCodeDialogService } from '@tv3/containers/rentals/custom-codes/dialogs/modify/modify-custom-code-dialog.service'

import { CustomCode } from '@tv3/store/custom-codes/custom-code.model'
import { MatPaginator } from '@angular/material/paginator'
import { MatSort } from '@angular/material/sort'
import { MatTableDataSource } from '@angular/material/table'
import { MatDialogRef } from '@angular/material/dialog'
import { CustomCodeFilters } from '@tv3/interfaces/filters/custom-code.filters'

@Component({
  selector: 'app-custom-codes',
  templateUrl: './custom-codes.component.html',
  styleUrls: ['./custom-codes.component.scss'],
})
export class CustomCodesComponent extends SelectableRow<CustomCode> implements OnInit, OnDestroy {
  @ViewChild('paginator', { static: true }) paginator: MatPaginator
  @ViewChild(MatSort, { static: true }) sort: MatSort

  displayedColumns = [
    'select',
    'name',
    'description',
    'body',
    'creatorView',
    'created',
    'rentalNames',
    'tagView',
    'edit',
  ]

  filters = this.filtersService.getCustomCodeFilters()
  currentFilters = []

  rentalOptions = []
  tagOptions = []
  currentCustomCodes: CustomCode[] = []
  allCustomCodes: CustomCode[] = []

  filterGroup = FilterGroup.CustomCode
  tableType = TableType.CustomCodesList
  isEmptyTable$ = isEmptyTable(this.dataSource)

  private destroy$ = new Subject()

  constructor(
    public dialogRef: MatDialogRef<CustomCodesComponent>,
    private toast: Toaster,
    private confirmDialog: ConfirmDialogService,
    private store: Store<fromRoot.State>,
    private filtersService: FiltersService,

    private modifyDialogService: ModifyCustomCodeDialogService,
    protected storage: UserStorage
  ) {
    super(storage, TableType.CustomCodesList)

    this.store.dispatch(GetAllCustomCodes())
  }

  ngOnInit() {
    this.dataSource.paginator = this.paginator
    if (this.savedSort) this.sort.sort(this.savedSort)
    this.dataSource.sort = this.sort
    this.dataSource.sortData = localeCompareSort

    this.store
      .pipe(
        select(selectUsersLoaded),
        tap((isLoaded) => {
          if (!isLoaded) {
            this.store.dispatch(LoadUsers())
          }
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {})

    this.filters.valueChanges
      .pipe(
        startWith(this.filters.getRawValue()),
        switchMap((filters) =>
          this.store.pipe(
            select(selectAllCustomCodeViews),
            map((customCodes) => ({ customCodes, filters }))
          )
        ),
        switchMap(({ customCodes, filters }) =>
          this.store.pipe(
            select(selectAllRentals),
            map((rentals) => ({ customCodes, filters, rentals }))
          )
        ),
        tap(({ customCodes, filters, rentals }) => this.filtersService.storeCustomCode(filters)),
        tap(({ customCodes, filters, rentals }) => this.updateFilters(customCodes, rentals)),
        tap(({ customCodes, filters, rentals }) => (this.currentFilters = filters)),
        map(
          ({
            customCodes,
            filters,
            rentals,
          }: {
            customCodes: CustomCode[]
            filters: CustomCodeFilters
            rentals: Rental[]
          }) => {
            this.allCustomCodes = customCodes

            if (isSomething(filters.rentals)) {
              customCodes = this.filterByRentals(customCodes, filters.rentals)
            }
            if (isSomething(filters.tags) || isSomething(filters.taggedIds)) {
              customCodes = this.filterByTags(customCodes, filters.tags, filters.taggedIds)
            }

            return customCodes
          }
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((customCodes) => {
        this.paginator.firstPage()
        this.dataSource.data = customCodes
        this.currentCustomCodes = customCodes
      })
  }

  ngOnDestroy() {
    this.destroy$.next(true)
    this.destroy$.complete()
  }

  onSearch(term: string) {
    this.paginator.firstPage()
    this.dataSource.filter = (term + '').trim().toLowerCase()
  }

  filterByTags(codes: CustomCode[], tags: string[], taggedIds: string[]): CustomCode[] {
    return R.filter(
      (code: CustomCode) =>
        R.any(
          R.equals(true),
          R.map((tag) => R.contains(tag, code.attributes.tags || []) || taggedIds?.includes(code.pkey), tags || [])
        ),
      codes
    )
  }

  filterByRentals(codes: CustomCode[], rentals: string[]): CustomCode[] {
    return R.filter(
      (code: CustomCode) =>
        R.any(
          R.equals(true),
          R.map((rental) => R.contains(rental, code.rentals || []), rentals)
        ),
      codes
    )
  }

  updateFilters(codes: CustomCode[], rentals: Rental[]) {
    const rentalIds = lodash.keyBy(rentals, (rental) => rental.id)

    const rentalOptions = R.filter(
      (item) => isSomething(item),
      R.map(
        (item) => rentalIds[item],
        R.uniq(
          R.reduce(
            (acc, item) => {
              return acc.concat(item.rentals)
            },
            [],
            codes
          )
        )
      )
    )
    this.rentalOptions = sortAscend('name')(rentalOptions)
    const tagOptions = R.map(
      (item) => {
        return { id: item, name: item }
      },
      R.filter(
        (item) => isSomething(item),
        R.uniq(
          R.reduce(
            (acc, item) => {
              return acc.concat(item.attributes.tags)
            },
            [],
            codes
          )
        )
      )
    )
    this.tagOptions = sortAscend('name')(<any>tagOptions).map((t: any) => t.id)
  }

  onCreate() {
    this.modifyDialogService.open()
  }

  onEdit(code: CustomCode) {
    this.modifyDialogService.open(code)
  }

  onDelete(code?: CustomCode) {
    const selected = isSomething(code) ? [code.pkey] : this.getSelected('pkey')

    if (selected.length < 1) {
      this.toast.warning($localize`Please select at least one custom code.`)
      return
    }

    this.confirmDialog
      .confirm({
        title: selected.length === 1 ? $localize`Delete this Custom Code?` : $localize`Delete these Custom Codes?`,
        body:
          selected.length === 1
            ? $localize`Are you sure you want to delete this Custom Code?`
            : $localize`Are you sure you want to delete these Custom Codes?`,
      })
      .subscribe(() => {
        this.store.dispatch(DeleteCustomCode({ ids: selected }))
      })
  }

  close() {
    this.dialogRef.close()
  }
}
